/**
 * Persistent dialog that appears after "Open in Google Sheets" runs.
 *
 * Two layouts:
 *   - DESKTOP: "CSV copied + Sheet tab open" — paste into A1.
 *   - MOBILE:  "CSV downloaded — open Sheets app + Import the file"
 *              (because the Sheets mobile app intercepts the desktop
 *              /create URL and shows "File not found").
 *
 * Mounted once at the AppShell root via registerSheetsInstructionsHandler
 * so any "Open in Sheets" caller anywhere in the app can trigger it.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon as CheckCircle, ClipboardTextIcon as ClipboardText, DownloadSimpleIcon as DownloadSimple, ArrowSquareOutIcon as ArrowSquareOut, DeviceMobileIcon as DeviceMobile, FileArrowDownIcon as FileArrowDown } from "@phosphor-icons/react";
import { registerSheetsInstructionsHandler } from "@/lib/google-sheets";

export default function GoogleSheetsInstructionsDialog() {
  const [state, setState] = useState({ open: false, copied: false, filename: "report.csv", mobile: false });

  useEffect(() => {
    registerSheetsInstructionsHandler((s) => setState({ open: true, ...s }));
  }, []);

  const close = () => setState((s) => ({ ...s, open: false }));

  return (
    <Dialog open={state.open} onOpenChange={(o) => setState((s) => ({ ...s, open: o }))}>
      <DialogContent className="max-w-md" data-testid="gsheets-instructions-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl tracking-tight">
            {state.mobile ? "Import into Google Sheets" : "Almost there"}
          </DialogTitle>
          <DialogDescription className="text-[#6B6B6B] text-sm">
            {state.mobile
              ? `${state.filename} downloaded. Here's how to open it in Sheets.`
              : "Your CSV is ready. Choose the option that worked."}
          </DialogDescription>
        </DialogHeader>

        {state.mobile ? <MobileBody filename={state.filename} /> : <DesktopBody copied={state.copied} filename={state.filename} />}

        <div className="flex justify-end gap-2 pt-3">
          <Button
            type="button"
            onClick={close}
            className="bg-ink hover:bg-crimson text-white rounded-full h-10 px-5"
            data-testid="gsheets-instructions-done"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DesktopBody({ copied, filename }) {
  return (
    <div className="space-y-3 pt-2">
      {copied ? (
        <Step
          icon={ClipboardText}
          tone="ok"
          title="CSV copied to your clipboard"
          body={
            <>
              Switch to the new Google Sheets tab, click cell <b>A1</b>, then press <kbd style={kbdStyle}>⌘V</kbd> on Mac or <kbd style={kbdStyle}>Ctrl + V</kbd> on Windows.
            </>
          }
        />
      ) : (
        <Step
          icon={ClipboardText}
          tone="warn"
          title="Couldn't copy to clipboard"
          body={<>Your browser blocked clipboard access. Use the file fallback below instead — it's just as fast.</>}
        />
      )}

      <Step
        icon={DownloadSimple}
        tone="info"
        title={`${filename} downloaded to your device`}
        body={
          <>
            In the Google Sheets tab, choose <b>File → Import</b>, click the <b>Upload</b> tab, and pick the file. Sheets will lay everything out as rows and columns.
          </>
        }
      />

      <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-[12px] text-[#6B6B6B] leading-relaxed">
        <p className="font-semibold text-ink mb-1 flex items-center gap-1.5">
          <ArrowSquareOut size={12} weight="bold" /> Sheets tab didn't open?
        </p>
        Your browser may have blocked the popup. Allow popups for this site, then try again — or just open <a className="underline text-ink" href="https://sheets.new" target="_blank" rel="noopener noreferrer">sheets.new</a> manually and paste / import from there.
      </div>
    </div>
  );
}

function MobileBody({ filename }) {
  return (
    <div className="space-y-3 pt-2">
      <Step
        icon={FileArrowDown}
        tone="ok"
        title="Step 1 — File saved"
        body={
          <>
            <b>{filename}</b> downloaded to your device (check the Files app on iPhone, or Downloads on Android).
          </>
        }
        stepNum={1}
      />
      <Step
        icon={DeviceMobile}
        tone="info"
        title="Step 2 — Open the Google Sheets app"
        body={<>If you don't have it, install <b>Google Sheets</b> from the App Store / Play Store first (free).</>}
        stepNum={2}
      />
      <Step
        icon={ArrowSquareOut}
        tone="info"
        title="Step 3 — Tap + then Import"
        body={
          <>
            In the Sheets app, tap the <b>+</b> button (bottom-right) → choose <b>New spreadsheet</b> → then in the new sheet tap <b>⋯</b> → <b>Share & export</b> → <b>Import</b>. Pick <b>{filename}</b> from your Files / Downloads.
          </>
        }
        stepNum={3}
      />
      <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-[12px] text-[#6B6B6B] leading-relaxed">
        <p className="font-semibold text-ink mb-1">Faster path on desktop</p>
        On a laptop, this same button auto-opens a blank Sheet and copies the data to your clipboard — just paste into A1.
      </div>
    </div>
  );
}

const kbdStyle = {
  background: "#FAFAFA",
  border: "1px solid #E5E5E5",
  borderRadius: "4px",
  padding: "1px 5px",
  fontSize: "11px",
  fontFamily: "monospace",
};

function Step({ icon: Icon, tone = "info", title, body, stepNum }) {
  const toneCls = {
    ok:   "bg-[#DCFCE7] text-[#15803D]",
    warn: "bg-[#FEF3C7] text-[#A16207]",
    info: "bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6]",
  }[tone] || "bg-ink/5 text-ink";

  const TitleIcon = tone === "ok" && !stepNum ? CheckCircle : Icon;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-[#E5E5E5] bg-white p-3">
      <span className={`inline-flex items-center justify-center h-9 w-9 rounded-full flex-shrink-0 ${toneCls}`}>
        <TitleIcon size={18} weight="duotone" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink text-[13px] mb-1">{title}</p>
        <p className="text-[12px] text-[#3F3F3F] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
